import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";

import { Cursor, Header, Footer, StripeBand, StickyMobileCta } from "@/pages/Site";
import { Seo } from "@/components/Seo";
import {
  SERVICE_PAGES,
  buildPageJsonLd,
} from "@/lib/service-pages";

const ORIGIN = "https://peanutfactorystudio.com";

/* List of related-service link cards shown in the footer of every service page. */
function RelatedServices({ currentSlug }) {
  const others = Object.values(SERVICE_PAGES).filter(
    (p) => p.slug !== currentSlug && !p.hidden,
  );
  return (
    <section className="py-20 md:py-28 bg-[#F3F3F3]" data-testid="related-services">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
          · the rest of what we offer
        </div>
        <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-4xl md:text-5xl mb-10">
          explore <span className="italic-serif text-[#FF5E00]">our other services</span>.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {others.map((p) => (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              data-testid={`related-${p.slug}`}
              className="group relative block overflow-hidden border border-[#FF5E00]/25 aspect-[4/5] bg-[#FF5E00] hover:scale-[1.01] transition-transform duration-300"
            >
              <img
                src={p.heroImage}
                alt={p.heroImageAlt}
                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
              <div className="absolute inset-0 p-5 flex flex-col justify-end text-white">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/80 mb-1">
                  {p.eyebrow}
                </div>
                <div className="font-display tracking-tighter text-2xl md:text-3xl leading-tight">
                  {p.h1.split(" — ")[0]}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* The hero block at the top of each service page. */
function ServiceHero({ page }) {
  return (
    <section className="relative pt-28 md:pt-40 pb-12" data-testid="service-hero">
      <div className="absolute left-0 right-0 top-20 md:top-24">
        <StripeBand height="h-[3px]" />
      </div>
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <nav className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#666] mb-6" aria-label="breadcrumb">
          <Link to="/" data-testid="breadcrumb-home" className="hover:text-[#FF5E00]">home</Link>
          <span>·</span>
          <Link to="/#services" data-testid="breadcrumb-services" className="hover:text-[#FF5E00]">services</Link>
          <span>·</span>
          <span className="text-[#FF5E00]">{page.eyebrow}</span>
        </nav>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <h1 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[0.95] text-5xl md:text-6xl lg:text-7xl">
              {page.h1}
            </h1>
          </div>

          <div className="col-span-12 lg:col-span-4 flex flex-col gap-5">
            <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/75">
              {page.intro}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={page.cta.href}
                data-testid="service-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-6 py-3 text-sm transition-all duration-300 hover:scale-[1.02]"
              >
                {page.cta.label} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#what-included"
                data-testid="service-cta-secondary"
                className="inline-flex items-center gap-2 rounded-full border border-[#FF5E00]/40 text-[#1A1A1A] hover:bg-[#FF5E00] hover:text-white hover:border-[#FF5E00] px-6 py-3 text-sm transition-colors duration-300"
              >
                what's included <ArrowDown className="w-4 h-4" />
              </a>
            </div>
            <div className="text-xs tracking-[0.22em] uppercase text-[#FF5E00] pt-2 border-t border-[#FF5E00]/25">
              {page.pricingLine}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceImage({ page }) {
  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 mt-6 md:mt-10 mb-16 md:mb-24">
      <div className="overflow-hidden bg-[#F3F3F3] aspect-[16/9]">
        <img
          src={page.heroImage}
          alt={page.heroImageAlt}
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          fetchpriority="high"
          width="1600"
          height="900"
        />
      </div>
    </div>
  );
}

/* Answer-first "quick answers" callout — formatted to be lifted verbatim by
   Google AI Overviews / ChatGPT / Perplexity as the studio's elevator pitch.
   Renders only when the page config supplies a `quickAnswers` array. */
function QuickAnswers({ items }) {
  if (!items?.length) return null;
  return (
    <section
      className="mx-auto max-w-[1000px] px-6 md:px-10 mb-12 md:mb-16"
      data-testid="service-quick-answers"
    >
      <div className="border border-[#FF5E00]/30 bg-[#FFF7F2] p-6 md:p-8">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
          · quick answers
        </div>
        <ul className="space-y-2.5">
          {items.map((q) => (
            <li
              key={q}
              className="flex gap-3 text-base md:text-lg leading-relaxed text-[#1A1A1A]"
            >
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#FF5E00] shrink-0" />
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ServiceSection({ section, anchor }) {
  return (
    <section
      id={anchor}
      className="mx-auto max-w-[900px] px-6 md:px-10 mb-12 md:mb-16"
      data-testid={`service-section-${anchor}`}
    >
      <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[1.05] text-3xl md:text-4xl mb-6">
        {section.h2}
      </h2>
      {section.intro && (
        <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/85 mb-6">
          {section.intro}
        </p>
      )}
      {section.kind === "list" ? (
        <ul className="space-y-3">
          {section.items.map((item) => (
            <li
              key={item}
              className="flex gap-3 text-base md:text-lg text-[#1A1A1A]/85 leading-relaxed"
            >
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#FF5E00] shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : section.kind === "subgroups" ? (
        <div className="space-y-8">
          {section.subgroups.map((sg) => (
            <div key={sg.h3}>
              <h3 className="font-display tracking-tight text-xl md:text-2xl text-[#1A1A1A] mb-3">
                {sg.h3}
              </h3>
              {sg.body && (
                <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/85 mb-3">
                  {sg.body}
                </p>
              )}
              {sg.items && (
                <ul className="space-y-2">
                  {sg.items.map((item) => (
                    <li
                      key={item}
                      className="flex gap-3 text-base md:text-lg text-[#1A1A1A]/85 leading-relaxed"
                    >
                      <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#FF5E00] shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-base md:text-lg leading-relaxed text-[#1A1A1A]/85">
          {section.body}
        </p>
      )}
    </section>
  );
}

function ServiceFaqs({ faqs }) {
  return (
    <section
      id="faqs"
      className="mx-auto max-w-[900px] px-6 md:px-10 mb-20 md:mb-28"
      data-testid="service-faqs"
    >
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
        · common questions
      </div>
      <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[1.05] text-3xl md:text-4xl mb-8">
        frequently <span className="italic-serif text-[#FF5E00]">asked</span>.
      </h2>
      <dl className="divide-y divide-[#FF5E00]/15 border-y border-[#FF5E00]/15">
        {faqs.map((f) => (
          <div key={f.q} className="py-5">
            <dt className="font-display tracking-tight text-lg md:text-xl text-[#1A1A1A] mb-2">
              {f.q}
            </dt>
            <dd className="text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed">
              {f.a}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function FinalCta({ page }) {
  return (
    <section className="bg-[#1A1A1A] text-white py-20 md:py-28" data-testid="service-final-cta">
      <div className="mx-auto max-w-[900px] px-6 md:px-10 text-center">
        <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl md:text-5xl mb-6">
          ready to <span className="italic-serif text-[#FF5E00]">shoot</span>?
        </h2>
        <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto mb-8">
          tell us your dates and brief — we get back same day with a quote.
        </p>
        <a
          href={page.cta.href}
          data-testid="service-final-cta-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF5E00] hover:bg-[#E04F00] text-white px-8 py-4 text-sm transition-all duration-300 hover:scale-[1.02]"
        >
          {page.cta.label} <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
}

export default function ServicePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = SERVICE_PAGES[slug];

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display tracking-tighter text-3xl mb-4">page not found</h1>
          <Link to="/" className="text-[#FF5E00] underline">back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="relative isolate overflow-hidden bg-[#FDFDFD]">
      <Seo
        title={page.title}
        description={page.description}
        canonical={`${ORIGIN}/${page.slug}`}
        image={page.heroImage}
        jsonLd={buildPageJsonLd(page)}
      />
      <Cursor />
      <div className="grain-overlay" aria-hidden />
      <Header />
      <ServiceHero page={page} />
      <ServiceImage page={page} />
      {page.gallery && page.gallery.length > 0 && (
        <section
          className="mx-auto max-w-[1400px] px-6 md:px-10 mb-20 md:mb-28"
          data-testid="service-gallery"
        >
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF5E00] mb-4">
            · {page.galleryEyebrow || "whats available on site"}
          </div>
          <h2 className="font-display font-medium tracking-tighter text-[#1A1A1A] leading-[1.05] text-3xl md:text-4xl mb-8">
            {page.galleryHeadingPrefix || "the"}{" "}
            <span className="italic-serif text-[#FF5E00]">{page.galleryHeadingAccent || "collection"}</span>.
          </h2>
          <div className={page.galleryGridClass || "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"}>
            {page.gallery.map((g, i) => (
              <div
                key={g.src}
                data-testid={`service-gallery-item-${i}`}
                className={`overflow-hidden bg-[#F3F3F3] ${page.galleryAspect || "aspect-[3/4]"}`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="block w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
            ))}
          </div>
        </section>
      )}
      <div id="what-included" />
      {page.sections.map((section, i) => (
        <ServiceSection key={section.h2} section={section} anchor={`section-${i}`} />
      ))}
      <ServiceFaqs faqs={page.faqs} />
      <FinalCta page={page} />
      <RelatedServices currentSlug={page.slug} />
      <Footer />
      <StickyMobileCta />
    </main>
  );
}
